import "./Commerce.css";

function MetricCard({ title, value, detail, tone = "blue" }) {
  return (
    <article className={`admin-commerce-metric-card tone-${tone}`}>
      <span>{title}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function PageShell({ eyebrow, title, description, metrics, toolbar, children }) {
  return (
    <section className="admin-commerce-page">
      <article className="admin-commerce-hero-card">
        <div className="admin-commerce-hero-copy">
          <p className="admin-commerce-eyebrow">{eyebrow}</p>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        {toolbar ? <div className="admin-commerce-toolbar">{toolbar}</div> : null}
      </article>

      {Array.isArray(metrics) && metrics.length ? (
        <section className="admin-commerce-metric-grid">
          {metrics.map((item) => (
            <MetricCard key={item.title} {...item} />
          ))}
        </section>
      ) : null}

      {children}
    </section>
  );
}

function OrderStatusBadge({ status, statusLabels, locale }) {
  const normalized = String(status || "").trim().toLowerCase();
  const toneMap = {
    paid: "positive",
    packed: "accent",
    in_transit: "warning",
    delivered: "positive",
    cancelled: "muted"
  };

  return (
    <span className={`admin-commerce-badge ${toneMap[normalized] || "muted"}`}>
      {statusLabels?.[normalized] || status || (locale === "zh" ? "未知" : "Unknown")}
    </span>
  );
}

export function AdminOrdersPage({
  locale,
  texts,
  adminOrders,
  view = "all",
  orderRows = adminOrders,
  paginatedOrderRows,
  renderPagination,
  statusLabels,
  orderStatusOptions,
  updateOrderStatus,
  onRefresh
}) {
  const isZh = locale === "zh";
  const sourceRows = Array.isArray(orderRows) ? orderRows : [];
  const totalRevenue = sourceRows.reduce((sum, item) => sum + (Number(item?.total || 0) || 0), 0);
  const paidCount = sourceRows.filter((item) => String(item?.status || "") === "paid").length;
  const transitCount = sourceRows.filter((item) => String(item?.status || "") === "in_transit").length;
  const deliveredCount = sourceRows.filter((item) => String(item?.status || "") === "delivered").length;
  const cancelledCount = sourceRows.filter((item) => String(item?.status || "") === "cancelled").length;
  const pageConfig = {
    all: {
      eyebrow: isZh ? "订单履约中心" : "Order Fulfillment Center",
      title: isZh ? "订单管理" : "Order Management",
      description: isZh ? "统一查看订单状态、金额和履约进度，快速推进待处理订单。" : "Track order status, revenue, and fulfillment progress from one clean operations view.",
      tableTitle: isZh ? "订单列表" : "Order List",
      tableDescription: isZh ? "支持直接在列表内调整订单状态。" : "Adjust fulfillment status directly in the list.",
      metrics: [
        { title: isZh ? "订单总数" : "Total Orders", value: sourceRows.length, detail: isZh ? "当前后台订单记录" : "Current order records", tone: "blue" },
        { title: isZh ? "待履约" : "Pending Flow", value: paidCount, detail: isZh ? "已付款待继续处理" : "Paid and waiting next step", tone: "violet" },
        { title: isZh ? "运输中" : "In Transit", value: transitCount, detail: isZh ? "需跟进物流节点" : "Requires logistics follow-up", tone: "amber" },
        { title: isZh ? "累计销售额" : "Gross Revenue", value: `US $${totalRevenue.toFixed(2)}`, detail: isZh ? `已完成 ${deliveredCount} 单` : `${deliveredCount} delivered orders`, tone: "emerald" }
      ]
    },
    paidQueue: {
      eyebrow: isZh ? "履约待办" : "Fulfillment Queue",
      title: isZh ? "待履约订单" : "Pending Fulfillment",
      description: isZh ? "聚焦已付款但还未进入运输阶段的订单，方便运营推进打包与发货。" : "Focus on paid orders that still need packaging and shipment handoff.",
      tableTitle: isZh ? "待履约清单" : "Pending Fulfillment Queue",
      tableDescription: isZh ? "这一页只保留当前需要继续推进的付款订单。" : "This view keeps only paid orders that still need fulfillment action.",
      metrics: [
        { title: isZh ? "待履约订单" : "Queue Size", value: sourceRows.length, detail: isZh ? "当前待继续处理" : "Orders waiting next action", tone: "blue" },
        { title: isZh ? "待推进金额" : "Queue Value", value: `US $${totalRevenue.toFixed(2)}`, detail: isZh ? "当前队列订单金额" : "Value locked in queue", tone: "violet" },
        { title: isZh ? "运输中参照" : "Transit Benchmark", value: transitCount, detail: isZh ? "与已发运订单对照" : "Compare against orders already shipped", tone: "amber" },
        { title: isZh ? "完成参照" : "Delivered Benchmark", value: deliveredCount, detail: isZh ? "帮助评估推进节奏" : "Gauge current throughput", tone: "emerald" }
      ]
    },
    inTransit: {
      eyebrow: isZh ? "物流跟踪" : "Transit Tracking",
      title: isZh ? "运输跟踪" : "In-transit Orders",
      description: isZh ? "把运输中的订单单独抽出，方便集中查看物流阶段与异常风险。" : "Pull in-transit orders into one focused view for shipment follow-up.",
      tableTitle: isZh ? "运输订单" : "In-transit Orders",
      tableDescription: isZh ? "适合客服、物流和销售协同跟进当前运输节点。" : "Useful for support, logistics, and sales to follow the current shipment stage.",
      metrics: [
        { title: isZh ? "运输中订单" : "Transit Orders", value: sourceRows.length, detail: isZh ? "当前物流跟踪对象" : "Orders under active tracking", tone: "blue" },
        { title: isZh ? "运输金额" : "Transit Value", value: `US $${totalRevenue.toFixed(2)}`, detail: isZh ? "在途订单金额" : "Revenue currently in transit", tone: "violet" },
        { title: isZh ? "已完成对照" : "Delivered Reference", value: deliveredCount, detail: isZh ? "对比交付完成量" : "Delivered baseline", tone: "emerald" },
        { title: isZh ? "异常风险" : "Risk Signals", value: cancelledCount, detail: isZh ? "结合取消单判断交付质量" : "Use cancelled orders as a quality signal", tone: "amber" }
      ]
    },
    completed: {
      eyebrow: isZh ? "交付结果" : "Delivery Results",
      title: isZh ? "已完成订单" : "Completed Orders",
      description: isZh ? "集中查看已交付订单，快速判断成交兑现与客户沉淀。" : "Review delivered orders to assess fulfillment quality and revenue realized.",
      tableTitle: isZh ? "已完成清单" : "Completed Orders",
      tableDescription: isZh ? "适合复盘成交结构与后续复购机会。" : "Use this view to review order mix and repeat-buy opportunities.",
      metrics: [
        { title: isZh ? "已完成订单" : "Delivered Orders", value: sourceRows.length, detail: isZh ? "当前完成交付" : "Orders already fulfilled", tone: "blue" },
        { title: isZh ? "已兑现营收" : "Recognized Revenue", value: `US $${totalRevenue.toFixed(2)}`, detail: isZh ? "已交付订单累计金额" : "Delivered order revenue", tone: "emerald" },
        { title: isZh ? "待履约参照" : "Pending Reference", value: paidCount, detail: isZh ? "帮助判断后续产能" : "Compare with orders still in queue", tone: "violet" },
        { title: isZh ? "运输中参照" : "Transit Reference", value: transitCount, detail: isZh ? "看近期交付接续" : "Track upcoming completions", tone: "amber" }
      ]
    },
    afterSales: {
      eyebrow: isZh ? "售后风险" : "After-sales Risk",
      title: isZh ? "取消与售后" : "Cancelled and After-sales",
      description: isZh ? "把取消订单集中出来，作为售后与履约质量的风险观察位。" : "Use cancelled orders as a compact after-sales and quality-risk panel.",
      tableTitle: isZh ? "取消订单" : "Cancelled Orders",
      tableDescription: isZh ? "当前数据里用取消订单承接售后观察位。" : "The current dataset uses cancelled orders as the after-sales observation slice.",
      metrics: [
        { title: isZh ? "取消订单" : "Cancelled Orders", value: sourceRows.length, detail: isZh ? "当前风险订单条目" : "Orders flagged as lost or cancelled", tone: "amber" },
        { title: isZh ? "风险金额" : "Risk Value", value: `US $${totalRevenue.toFixed(2)}`, detail: isZh ? "需关注的流失金额" : "Revenue exposed to loss", tone: "violet" },
        { title: isZh ? "已完成对照" : "Delivered Reference", value: deliveredCount, detail: isZh ? "反向观察履约质量" : "Benchmark fulfilled quality", tone: "emerald" },
        { title: isZh ? "运输中对照" : "Transit Reference", value: transitCount, detail: isZh ? "识别运输阶段风险外溢" : "See whether issues spill from transit", tone: "blue" }
      ]
    }
  };
  const currentConfig = pageConfig[view] || pageConfig.all;

  return (
    <PageShell
      eyebrow={currentConfig.eyebrow}
      title={currentConfig.title}
      description={currentConfig.description}
      toolbar={
        <button type="button" className="controlhub-button ghost" onClick={() => onRefresh?.()}>
          {texts.refresh}
        </button>
      }
      metrics={currentConfig.metrics}
    >
      <article className="admin-commerce-surface-card">
        <div className="admin-commerce-section-head">
          <div>
            <h4>{currentConfig.tableTitle}</h4>
            <p>{currentConfig.tableDescription}</p>
          </div>
        </div>

        <div className="admin-commerce-table-wrap">
          <table className="admin-commerce-table">
            <thead>
              <tr>
                <th>{texts.orderId}</th>
                <th>{texts.userId}</th>
                <th>{texts.total}</th>
                <th>{texts.status}</th>
                <th>{isZh ? "状态标签" : "Status Tag"}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrderRows.items.map((item) => (
                <tr key={item.id}>
                  <td>#{item.id}</td>
                  <td>{item.userId}</td>
                  <td>{item.total} {item.currency}</td>
                  <td>
                    <select
                      className="admin-commerce-select"
                      value={item.status}
                      onChange={(event) => updateOrderStatus(item.id, event.target.value)}
                    >
                      {orderStatusOptions.map((option) => (
                        <option key={option} value={option}>
                          {statusLabels[option] || option}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <OrderStatusBadge status={item.status} statusLabels={statusLabels} locale={locale} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination("orders", paginatedOrderRows)}
      </article>
    </PageShell>
  );
}

export function AdminCustomersPage({ locale, texts, view = "records", paginatedCustomerRows, renderPagination, adminUsers }) {
  const isZh = locale === "zh";
  const sourceRows = Array.isArray(adminUsers) ? adminUsers : [];
  const activeCount = sourceRows.filter((item) => String(item?.status || "active").toLowerCase() !== "disactive").length;
  const adminCount = sourceRows.filter((item) => String(item?.role || "").toLowerCase().includes("admin")).length;
  const totalCartItems = sourceRows.reduce((sum, item) => sum + (Number(item?.cartItems || 0) || 0), 0);
  const customerLikeRows = sourceRows.filter((item) => ["customer", "guest"].includes(String(item?.role || "").trim().toLowerCase()));
  const roleSegments = Array.from(
    customerLikeRows.reduce((map, item) => {
      const key = String(item?.role || (isZh ? "客户" : "customer")).trim() || (isZh ? "客户" : "customer");
      map.set(key, Number(map.get(key) || 0) + 1);
      return map;
    }, new Map()).entries()
  ).sort((left, right) => right[1] - left[1]).slice(0, 4);
  const tagSignals = [
    { label: isZh ? "高意向购物车" : "High-intent carts", value: sourceRows.filter((item) => Number(item?.cartItems || 0) >= 3).length },
    { label: isZh ? "沉默账户" : "Dormant accounts", value: sourceRows.filter((item) => String(item?.status || "active").toLowerCase() === "disactive").length },
    { label: isZh ? "管理员客户" : "Admin-linked accounts", value: adminCount },
    { label: isZh ? "零购物车" : "Zero-cart users", value: sourceRows.filter((item) => Number(item?.cartItems || 0) <= 0).length }
  ];
  const pageConfig = {
    records: {
      eyebrow: isZh ? "客户运营中心" : "Customer Operations",
      title: isZh ? "客户管理" : "Customer Management",
      description: isZh ? "查看客户信息、角色和购物车活跃度，帮助销售与客服快速筛选重点用户。" : "Review customer records, roles, and cart activity to prioritize support and sales follow-up.",
      tableTitle: isZh ? "客户档案" : "Customer Records",
      tableDescription: isZh ? "保留现有数据结构，仅升级展示层级和表格样式。" : "Keeping the current data structure while upgrading hierarchy and table presentation.",
      summaryRows: []
    },
    segments: {
      eyebrow: isZh ? "客户结构视图" : "Customer Segmentation",
      title: isZh ? "客户分层" : "Customer Segments",
      description: isZh ? "把客户角色和活跃度拆成可读分层，便于快速判断重点服务对象。" : "Turn raw customer rows into a readable segmentation view for priority handling.",
      tableTitle: isZh ? "分层客户清单" : "Segmented Customer List",
      tableDescription: isZh ? "当前先按角色结构和活跃度承接分层视图。" : "The current segmentation view uses role structure and activity as the first layer.",
      summaryRows: roleSegments.map(([label, value]) => ({ label, value }))
    },
    tags: {
      eyebrow: isZh ? "标签洞察" : "Tag Insights",
      title: isZh ? "客户标签洞察" : "Customer Tag Insights",
      description: isZh ? "先用购物车活跃度、沉默状态和管理角色生成运营可用的标签观察位。" : "Use cart activity, dormant status, and role signals as an operations-ready tag view.",
      tableTitle: isZh ? "标签候选客户" : "Tag Candidate Customers",
      tableDescription: isZh ? "这一页承接后续更细的 CRM 标签能力。" : "This page serves as the bridge to more granular CRM tagging later.",
      summaryRows: tagSignals
    }
  };
  const currentConfig = pageConfig[view] || pageConfig.records;

  return (
    <PageShell
      eyebrow={currentConfig.eyebrow}
      title={currentConfig.title}
      description={currentConfig.description}
      metrics={[
        { title: isZh ? "客户总数" : "Total Customers", value: sourceRows.length, detail: isZh ? "全部用户档案" : "All customer records", tone: "blue" },
        { title: isZh ? "活跃账户" : "Active Accounts", value: activeCount, detail: isZh ? "当前可正常使用" : "Currently active", tone: "emerald" },
        { title: isZh ? "管理角色" : "Admin Roles", value: adminCount, detail: isZh ? "含管理员及高级角色" : "Admin and elevated roles", tone: "violet" },
        { title: isZh ? "购物车商品数" : "Cart Items", value: totalCartItems, detail: isZh ? "用于判断采购意向" : "Signal for purchase intent", tone: "amber" }
      ]}
    >
      <article className="admin-commerce-surface-card">
        <div className="admin-commerce-section-head">
          <div>
            <h4>{currentConfig.tableTitle}</h4>
            <p>{currentConfig.tableDescription}</p>
          </div>
        </div>

        {currentConfig.summaryRows.length ? (
          <div className="admin-commerce-panel-pills" aria-label={isZh ? "客户摘要" : "Customer Summary"} style={{ marginBottom: 16, flexWrap: "wrap" }}>
            {currentConfig.summaryRows.map((item) => (
              <span key={item.label} className="admin-commerce-panel-pill">
                {item.label}: {item.value}
              </span>
            ))}
          </div>
        ) : null}

        <div className="admin-commerce-table-wrap">
          <table className="admin-commerce-table">
            <thead>
              <tr>
                <th>{texts.id}</th>
                <th>{texts.name}</th>
                <th>{texts.email}</th>
                <th>{texts.role}</th>
                <th>{texts.cartItems}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCustomerRows.items.map((item) => (
                <tr key={item.id}>
                  <td>#{item.id}</td>
                  <td>{item.name || "-"}</td>
                  <td>{item.email || "-"}</td>
                  <td>
                    <span className="admin-commerce-badge accent">{item.role || "customer"}</span>
                  </td>
                  <td>{item.cartItems || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination("customers", paginatedCustomerRows)}
      </article>
    </PageShell>
  );
}

export function AdminProductsPage({
  locale,
  texts,
  adminProducts,
  analyticsByProductId,
  analyticsDailyByProductId,
  analyticsLoading,
  analyticsRange,
  onChangeAnalyticsRange,
  onRefreshAnalytics,
  onExportAnalyticsDaily,
  analyticsExporting,
  onStartEdit,
  onDeleteProduct,
  onOpenCreate,
  paginatedProductRows,
  renderPagination,
  productsTable
}) {
  const isZh = locale === "zh";
  const publishedCount = adminProducts.filter((item) => Number(item?.priceUSD || 0) > 0 && String(item?.imageUrl || "").trim()).length;
  const catalogCount = new Set(adminProducts.map((item) => String(item?.category || "Others"))).size;
  const pricedCount = adminProducts.filter((item) => Number(item?.priceUSD || 0) > 0).length;
  const trackedCount = adminProducts.filter((item) => analyticsByProductId?.[String(item?.id)]).length;
  const visibleCount = Array.isArray(paginatedProductRows?.items) ? paginatedProductRows.items.length : 0;
  const draftCount = Math.max(0, adminProducts.length - publishedCount);

  return (
    <PageShell
      eyebrow={isZh ? "商品内容中心" : "Product Content Center"}
      title={isZh ? "商品管理" : "Product Management"}
      description={isZh ? "围绕商品资料、分析趋势和发布效率组织页面结构，保留原有编辑与删除能力。" : "Reframe the product workspace around content quality, analytics, and publishing efficiency while preserving existing edit and delete flows."}
      toolbar={
        onOpenCreate ? (
          <button type="button" className="controlhub-button" onClick={onOpenCreate}>
            {texts.createProduct}
          </button>
        ) : null
      }
      metrics={[
        { title: isZh ? "商品总数" : "Catalog Items", value: adminProducts.length, detail: isZh ? "当前后台商品记录" : "Current product records", tone: "blue" },
        { title: isZh ? "已具备售价" : "Priced Items", value: pricedCount, detail: isZh ? "已录入价格信息" : "Price information ready", tone: "violet" },
        { title: isZh ? "可直接上架" : "Ready to Publish", value: publishedCount, detail: isZh ? "图片与售价同时具备" : "Has both price and cover image", tone: "emerald" },
        { title: isZh ? "品类数量" : "Categories", value: catalogCount, detail: isZh ? "当前商品覆盖类目" : "Active category coverage", tone: "amber" }
      ]}
    >
      <article className="admin-commerce-surface-card admin-commerce-product-host">
        <div className="admin-commerce-section-head">
          <div>
            <h4>{isZh ? "商品表现与编辑" : "Product Performance and Editing"}</h4>
            <p>{isZh ? "下方保留原有商品分析、趋势图和编辑操作。" : "The original analytics, trend sparkline, and editing controls remain available below."}</p>
          </div>
          <div className="admin-commerce-panel-pills" aria-label={isZh ? "商品面板摘要" : "Product panel summary"}>
            <span className="admin-commerce-panel-pill is-primary">
              {isZh ? `当前页 ${visibleCount} 条` : `${visibleCount} visible`}
            </span>
            <span className="admin-commerce-panel-pill">
              {isZh ? `已跟踪分析 ${trackedCount}` : `${trackedCount} tracked`}
            </span>
            <span className="admin-commerce-panel-pill is-muted">
              {isZh ? `待完善 ${draftCount}` : `${draftCount} drafts`}
            </span>
          </div>
        </div>
        <div className="admin-commerce-product-body">
          {productsTable}
        </div>
        <div className="admin-commerce-panel-footer">
          <div className="admin-commerce-panel-footer-note">
            {isZh
              ? `已覆盖 ${catalogCount} 个品类，${pricedCount} 个商品已补齐价格信息。`
              : `${catalogCount} categories covered, with pricing ready on ${pricedCount} items.`}
          </div>
          {renderPagination("products", paginatedProductRows)}
        </div>
      </article>
    </PageShell>
  );
}